<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { computed, markRaw, onMounted, ref, watch } from 'vue';

import MainTitleComponent from '@/components/ui/MainTitleComponent.vue';
import { formatCurrency, formatDate, formatInvestmentStatus, formatItemValue } from '@/utils/formatters.ts';
import BankService from "@/services/bank/bankService.ts";
import { InvestmentStatusKey } from "@/enums/bank_enum.ts";
import { displayToast } from "@/utils/toastUtils.ts";
import { displayModal } from "@/utils/modalUtils.ts";
import { useDrawer } from '@/stores/useDrawer.ts';
import OperationStatusComponent from "@/components/bank/OperationStatusComponent.vue";

import type { PaginatedQuery, SortItem, SortOrder } from '@/types/services/utils/request.type.ts';
import type { PaginatedResponse } from '@/types/services/utils/response.type.ts';
import type { DataTableHeader } from 'vuetify/framework';
import type {
  InvestmentOperationResponse,
  InvestmentOperationsSortKey,
} from "@/types/services/bank/bank.type.ts";
import DetailInvestmentView from '@/views/bank/investment/DetailInvestmentView.vue';

const { t } = useI18n();
const { openDrawer } = useDrawer()

const investmentOperations = ref<InvestmentOperationResponse[]>([]);
const loading = ref<boolean>(false);

const search = ref<string>('');
const sort = ref<InvestmentOperationsSortKey>('createdAt');
const order = ref<SortOrder>('desc');
const limit = ref<number>(10);
const page = ref<number>(1);

const total = ref<number>(0);
const maxPages = ref<number>(1);

const headers = computed<DataTableHeader[]>(() => [
  {
    title: t('views.investmentList.header.username'),
    key: 'username',
    sortable: true,
    align: 'start'

  },
  {
    title: t('views.investmentList.header.reference'),
    key: 'reference',
    sortable: true,
    align: 'start'
  },
  {
    title: t('views.investmentList.header.investmentAmount'),
    key: 'investmentAmount',
    sortable: true,
    align: 'start'
  },
  {
    title: t('views.investmentList.header.createdAt'),
    key: 'createdAt',
    sortable: true,
    align: 'start'
  },
  {
    title: t('views.investmentList.header.status'),
    key: 'status',
    sortable: true,
    align: 'start'
  },
  {
    title: t('views.investmentList.header.actions'),
    key: 'actions',
    sortable: false,
    width: '100px',
    align: 'end'
  },
]);

onMounted(fetchInvestmentOperations);

watch([search, sort, order, limit, page], fetchInvestmentOperations);

function canUpdate(investment: InvestmentOperationResponse) {
  return investment.status === InvestmentStatusKey.PAYMENT_PENDING;
}

async function fetchInvestmentOperations(): Promise<void> {
  try {
    loading.value = true;

    const params: PaginatedQuery = {
      search: search.value,
      sort: sort.value,
      order: order.value,
      limit: limit.value,
      page: page.value
    };

    const response: PaginatedResponse<InvestmentOperationResponse> =
      await BankService.getPaginatedInvestmentOperations(params);

    investmentOperations.value = response.data;
    total.value = response.total;
    maxPages.value = response.maxPages;
  } finally {
    loading.value = false;
  }
}

function onSort(sortBy: SortItem<InvestmentOperationsSortKey>[]): void {
  const first = sortBy?.[0];
  sort.value = first.key;
  order.value = first.order;
}

async function onRowClick(_: MouseEvent, row: { item: InvestmentOperationResponse }): Promise<void> {
  openInvestmentDetail(row.item)
}

function openInvestmentDetail(investment: InvestmentOperationResponse) {
  openDrawer({
    title: t('views.detailInvestment.title'),
    component: DetailInvestmentView,
    props: {
      key: investment.contractId + '-' + Date.now(),
      contractId: investment.contractId,
      drawerMode: true,
      onStatusUpdated: async () => {
        await fetchInvestmentOperations()
        useDrawer().closeDrawer()
      }
    },
    width: 520,
  })
}

// --- Expire investment ---
async function expireInvestment(investment: InvestmentOperationResponse) {
  const modal = await displayModal({
    component: markRaw(OperationStatusComponent),
    props: {
      title: t('views.detailInvestment.modal.expire.title'),
      message: t('views.detailInvestment.modal.expire.message'),
    },
  });

  if (modal) {
    try {
      loading.value = true;

      await BankService.updateInvestmentStatus(investment.contractId, InvestmentStatusKey.EXPIRED);

      displayToast(t('views.detailInvestment.expireSuccess'), 'success');
      investment.status = InvestmentStatusKey.EXPIRED;
    } finally {
      loading.value = false;
    }
  }
}

// --- Validate payment ---
async function validatePayment(investment: InvestmentOperationResponse) {
  const modal = await displayModal({
    component: markRaw(OperationStatusComponent),
    props: {
      title: t('views.detailInvestment.modal.validate.title'),
      message: t('views.detailInvestment.modal.validate.message'),
    },
  });

  if (modal) {
    try {
      loading.value = true;

      await BankService.updateInvestmentStatus(investment.contractId, InvestmentStatusKey.VALIDATED);

      displayToast(t('views.detailInvestment.validatedSuccess'), 'success');
      investment.status = InvestmentStatusKey.VALIDATED;
    } finally {
      loading.value = false;
    }
  }
}

// --- Cancel Investment ---
async function cancelInvestment(investment: InvestmentOperationResponse) {
  const modal = await displayModal({
    component: markRaw(OperationStatusComponent),
    props: {
      title: t('views.detailInvestment.modal.cancel.title'),
      message: t('views.detailInvestment.modal.cancel.message'),
    },
  });

  if (modal) {
    try {
      loading.value = true;

      await BankService.updateInvestmentStatus(investment.contractId, InvestmentStatusKey.CANCELED);

      displayToast(t('views.detailInvestment.cancelSuccess'), 'success');
      investment.status = InvestmentStatusKey.CANCELED;
    } finally {
      loading.value = false;
    }
  }
}
</script>

<template>
  <!-- Main title -->
  <MainTitleComponent icon="PhArrowsLeftRight">
    {{ t("views.investmentList.title") }}
  </MainTitleComponent>

  <v-row>
    <v-col cols="12" sm="5">
      <v-text-field
        v-model="search"
        :label="t('views.investmentList.search')"
        append-inner-icon="PhMagnifyingGlass"
        density="compact"
        hide-details
      />
    </v-col>
  </v-row>

  <v-row>
    <v-col>
      <v-data-table-server
        :headers="headers"
        :items="investmentOperations"
        :items-length="total"
        :items-per-page="limit"
        :loading="loading"
        :no-data-text="t('common.noResult')"
        :page="page"
        :sort-by="[{ key: sort, order: order }]"
        hover
        must-sort
        @update:page="page = $event"
        @update:items-per-page="limit = $event"
        @update:sort-by="onSort"
        @click:row="onRowClick"
      >
        <template #loading>
          <v-skeleton-loader type="table-row@4" />
        </template>
        <template #[`item.username`]="{ item }">
          <div class="d-flex flex-column align-items-center">
            {{ formatItemValue(item.username) }}
            <span class="text-caption text-medium-emphasis">
              {{ formatItemValue(item.email) }}
            </span>
          </div>
        </template>
        <template #[`item.reference`]="{ item }">
          {{ formatItemValue(item.reference) }}
        </template>
        <template #[`item.investmentAmount`]="{ item }">
          {{ formatCurrency(item.investmentAmount) }}
        </template>
        <template #[`item.createdAt`]="{ item }">
          {{ formatDate(item.createdAt) }}
        </template>
        <template #[`item.status`]="{ item }">
          <v-chip
            :color="formatInvestmentStatus(item.status)"
            text-color="white"
            class="ma-2"
          >
            {{ t('views.investmentList.status.' + item.status) }}
          </v-chip>
        </template>
        <template #[`item.actions`]="{ item }">
          <v-menu>
            <template #activator="{ props }">
              <button type="button" v-bind="props">
                <v-icon icon="PhDotsThreeVertical" />
              </button>
            </template>

            <v-list>
              <v-list-item  @click.prevent="openInvestmentDetail(item)">
                <template #default>
                  <p class="text-subtitle-2">
                    {{ t('views.investmentList.table.actions.detail') }}
                  </p>
                </template>
                <template #prepend>
                  <v-icon size="small" icon="PhEye" />
                </template>
              </v-list-item>

              <v-list-item
                v-if="canUpdate(item)"
                @click.prevent="validatePayment(item)"
              >
                <template #default>
                  <p class="text-subtitle-2">
                    {{ t('views.investmentList.table.actions.validate') }}
                  </p>
                </template>
                <template #prepend>
                  <v-icon size="small" icon="PhCheck" />
                </template>
              </v-list-item>

              <v-list-item
                v-if="canUpdate(item)"
                @click.prevent="cancelInvestment(item)"
              >
                <template #default>
                  <p class="text-subtitle-2">
                    {{ t('views.investmentList.table.actions.cancel') }}
                  </p>
                </template>
                <template #prepend>
                  <v-icon size="small" icon="PhX" />
                </template>
              </v-list-item>

              <v-list-item
                v-if="canUpdate(item)"
                @click.prevent="expireInvestment(item)"
              >
                <template #default>
                  <p class="text-subtitle-2">
                    {{ t('views.investmentList.table.actions.expire') }}
                  </p>
                </template>
                <template #prepend>
                  <v-icon size="small" icon="PhHourglass" />
                </template>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
        <template #bottom>
          <v-pagination
            v-model="page"
            :length="maxPages"
            :tile="true"
            density="compact"
            total-visible="6"
          />
        </template>
      </v-data-table-server>
    </v-col>
  </v-row>
</template>
